import { Button } from '@calizahq/react-components-v2'
import {
  InclusiveExclusive,
  usePotentialBookingFiltersContext,
} from '@sales-app/context/potentialBookingFilters/potentialBookingFilters'
import { StatusQueries } from '@sales-app/graphql/queries/potentialBooking/status.queries'
import pull from 'lodash/pull'
import React, { useEffect, useMemo, useState } from 'react'

import { Checkbox, Dropdown } from '@/components'
import { useMobile } from '@/hooks'
import { useGraphql } from '@/hooks/useGraphql'

type Props = {
  className?: string
}

export const StatusFilter = ({ className }: Props) => {
  const { potentialBookingStatuses, setFilters } = usePotentialBookingFiltersContext()

  const [dropdownExpanded, setDropdownExpanded] = useState(false)
  const { isMobile } = useMobile()

  const [selectedOptions, setSelectedOptions] = useState<string[]>([''])

  const { data } = useGraphql<StatusQueries.GET_STATUSES.Response>(
    StatusQueries.getStatuses.name,
    StatusQueries.getStatuses(),
    {
      filter: {
        active: true,
        standard: true,
        corporateHousing: false,
      },
    },
  )

  const options = useMemo(
    () => data?.potentialBookingStatuses?.map((s) => s.description) || [],
    [data?.potentialBookingStatuses],
  )

  useEffect(() => {
    if (potentialBookingStatuses?.inclusive === InclusiveExclusive.EXCLUSIVE) {
      setSelectedOptions(options.filter((element) => !potentialBookingStatuses?.descriptions?.includes(element)) || [])
    } else {
      setSelectedOptions(potentialBookingStatuses?.descriptions || [])
    }
  }, [potentialBookingStatuses, options])

  const handleClick = (description: string) => {
    if (selectedOptions.includes(description)) setSelectedOptions(pull([...selectedOptions], description))
    else setSelectedOptions([...selectedOptions, description])
  }

  const handleApply = () => {
    setDropdownExpanded(false)

    setFilters({
      potentialBookingStatuses: {
        inclusive: InclusiveExclusive.INCLUSIVE,
        descriptions: selectedOptions,
      },
    })
  }

  const handleResetFilters = () => {
    setDropdownExpanded(false)
    setSelectedOptions([])

    setFilters({
      potentialBookingStatuses: null,
    })
  }

  const handleCancel = () => {
    setDropdownExpanded(false)
  }

  const renderButtons = () => {
    return (
      <div className="flex flex-row items-center justify-between px-6 pb-4 pt-4">
        {isMobile ? (
          <Button className="mr-4 flex-1 bg-grey-100" onClick={handleResetFilters} variant={'secondary'}>
            Reset
          </Button>
        ) : (
          <Button onClick={handleResetFilters} size="small" variant={'secondary'}>
            <span className="underline underline-offset-2">Reset</span>
          </Button>
        )}

        <Button
          className="flex-1 bg-text-semantic-success md:flex-0"
          onClick={handleApply}
          size={isMobile ? undefined : 'small'}
        >
          Apply
        </Button>
      </div>
    )
  }

  const renderHeader = () => {
    return (
      <div className="md:px-6 md:pt-6 md:mb-4">
        <h3 className="font-bold text-header-md">Status</h3>
      </div>
    )
  }

  return (
    <Dropdown
      className={className}
      displayedCount={selectedOptions?.length ?? 0}
      expanded={dropdownExpanded}
      headerTitle="Status"
      onHideDropdown={handleCancel}
      renderFooter={renderButtons}
      renderHeader={renderHeader}
      selected={!!potentialBookingStatuses}
      setExpanded={setDropdownExpanded}
    >
      <div className="md:w-120">
        <div className={`grid max-h-72 grid-cols-2 gap-x-14 gap-y-4 px-6 pb-6 md:overflow-auto`}>
          {options.map((val) => (
            <div
              className="flex cursor-pointer flex-row items-center"
              key={val}
              onClick={() => handleClick(val)}
              role="presentation"
            >
              <Checkbox checked={selectedOptions.includes(val)} onClick={() => handleClick(val)} />
              <div className="text-sm">{val}</div>
            </div>
          ))}
          <div className="h-8 col-span-2 md:hidden" />
        </div>
      </div>
    </Dropdown>
  )
}
